// F-MOTOR-RESERVAS.3.0.4 — Recibo com identidade oficial Rede Hospeda.
// Paleta e logo via src/lib/pdfBrand.js. Nenhuma alteração em lógica, valores,
// numberToWords, valorPorExtenso ou geração do arquivo.
import { jsPDF } from "jspdf";
import { FISCAL } from "./fiscal";
import { PDF_BRAND, loadBrandLogoDataUrl } from "./pdfBrand";

const UNITS = ["", "um", "dois", "três", "quatro", "cinco", "seis", "sete", "oito", "nove"];
const TEEN = ["dez", "onze", "doze", "treze", "catorze", "quinze", "dezesseis", "dezessete", "dezoito", "dezenove"];
const TENS = ["", "", "vinte", "trinta", "quarenta", "cinquenta", "sessenta", "setenta", "oitenta", "noventa"];
const HUNDREDS = ["", "cento", "duzentos", "trezentos", "quatrocentos", "quinhentos", "seiscentos", "setecentos", "oitocentos", "novecentos"];

function threeDigit(n) {
  if (n === 0) return "";
  if (n === 100) return "cem";
  const c = Math.floor(n / 100);
  const rest = n % 100;
  const parts = [];
  if (c) parts.push(HUNDREDS[c]);
  if (rest < 10) { if (rest) parts.push(UNITS[rest]); }
  else if (rest < 20) parts.push(TEEN[rest - 10]);
  else {
    const t = Math.floor(rest / 10);
    const u = rest % 10;
    if (u) parts.push(TENS[t] + " e " + UNITS[u]);
    else parts.push(TENS[t]);
  }
  return parts.join(" e ");
}

function numberToWords(n) {
  n = Math.floor(n);
  if (n === 0) return "zero";
  const thousands = Math.floor(n / 1000);
  const rest = n % 1000;
  const parts = [];
  if (thousands) {
    if (thousands === 1) parts.push("mil");
    else parts.push(threeDigit(thousands) + " mil");
  }
  if (rest) {
    if (thousands && rest < 100) parts.push("e " + threeDigit(rest));
    else parts.push(threeDigit(rest));
  }
  return parts.filter(Boolean).join(" ");
}

export function valorPorExtenso(valor) {
  const v = Number(valor || 0);
  const intPart = Math.floor(v);
  const cents = Math.round((v - intPart) * 100);
  let words = numberToWords(intPart) + " " + (intPart === 1 ? "real" : "reais");
  if (cents) words += " e " + numberToWords(cents) + " " + (cents === 1 ? "centavo" : "centavos");
  return words;
}

const brl = (n) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(n || 0));
const iso2br = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export async function generateReceiptPDF(r) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const { AZUL, AZUL_PROFUNDO, DOURADO, CREME, INK, MUTED, DIVIDER, FONT_TITLE, FONT_BODY } = PDF_BRAND;

  const logoDataUrl = await loadBrandLogoDataUrl();

  // Moldura externa azul oficial
  doc.setDrawColor(...AZUL);
  doc.setLineWidth(1.5);
  doc.rect(30, 30, W - 60, 782);

  // Faixa superior (azul oficial)
  doc.setFillColor(...AZUL);
  doc.rect(30, 30, W - 60, 120, "F");

  // Logo oficial à esquerda (altura 56pt → width ~143pt)
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, "PNG", 50, 44, 140, 55);
    } catch (err) {
      _drawWordmarkFallback(doc);
    }
  } else {
    _drawWordmarkFallback(doc);
  }

  doc.setTextColor(...CREME);
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(10);
  doc.text("Recibo  ·  Gestão de Pousadas", 50, 120);

  // Dados da pousada à direita
  const rightX = W - 50;
  doc.setFont(FONT_BODY, "bold");
  doc.setFontSize(10);
  doc.text(r.pousada_nome || "—", rightX, 60, { align: "right" });
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(8);
  const enderecoLines = doc.splitTextToSize(r.pousada_endereco || "", 280);
  let ry = 74;
  enderecoLines.slice(0, 2).forEach((line) => {
    doc.text(line, rightX, ry, { align: "right" });
    ry += 11;
  });
  if (r.pousada_telefone) {
    doc.text(`Tel: ${r.pousada_telefone}`, rightX, ry, { align: "right" });
    ry += 11;
  }
  if (r.pousada_email) {
    doc.text(r.pousada_email, rightX, ry, { align: "right" });
    ry += 11;
  }

  // Nº + data
  doc.setFontSize(9);
  doc.text(`Nº ${String(r.id || "").slice(0, 8).toUpperCase()}  ·  ${new Date().toLocaleDateString("pt-BR")}`, 50, 138);

  // Faixa dourada oficial
  doc.setFillColor(...DOURADO);
  doc.rect(30, 150, W - 60, 4, "F");

  // Painel do valor (fundo creme oficial)
  doc.setFillColor(...CREME);
  doc.setDrawColor(...DIVIDER);
  doc.rect(60, 180, W - 120, 90, "FD");
  // Faixa dourada lateral
  doc.setFillColor(...DOURADO);
  doc.rect(60, 180, 5, 90, "F");

  doc.setTextColor(...MUTED);
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(10);
  doc.text("VALOR RECEBIDO", 85, 205);
  doc.setTextColor(...AZUL);
  doc.setFont(FONT_TITLE, "bold");
  doc.setFontSize(36);
  doc.text(brl(r.valor_total), 85, 250);

  // Corpo
  doc.setTextColor(...INK);
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(11);
  const wrap = (text, y, opts = {}) => {
    const lines = doc.splitTextToSize(text, W - 120);
    doc.text(lines, 60, y, opts);
    return y + lines.length * 16;
  };

  const empresaLine = r.empresa_nome
    ? `${r.empresa_nome}${r.empresa_cnpj ? ` — CNPJ ${r.empresa_cnpj}` : ""}`
    : null;

  const pagador = empresaLine || r.hospede_nome || "—";
  const hospedeSuffix = empresaLine
    ? ` — hóspede: ${r.hospede_nome || "—"}${r.hospede_documento ? ` (doc. ${r.hospede_documento})` : ""}`
    : (r.hospede_documento ? ` (doc. ${r.hospede_documento})` : "");

  let y = wrap(`Recebi de ${pagador}${hospedeSuffix} a importância de:`, 310);
  doc.setFont(FONT_BODY, "italic");
  doc.setTextColor(...AZUL_PROFUNDO);
  y = wrap(`«${valorPorExtenso(r.valor_total)}»`, y + 8);
  doc.setTextColor(...INK);
  doc.setFont(FONT_BODY, "normal");

  const descr =
    `referente à hospedagem na ${r.pousada_nome || "—"}, quarto ${r.quarto_numero || "—"}` +
    `${r.quarto_tipo ? ` (${r.quarto_tipo})` : ""}` +
    `, no período de ${iso2br(r.data_entrada)} a ${iso2br(r.data_saida)}` +
    `, totalizando ${r.diarias} ${r.diarias === 1 ? "diária" : "diárias"} de ${brl(r.valor_diaria)}.`;
  y = wrap(descr, y + 18);

  y = wrap(
    "Para clareza, firmo o presente recibo, dando quitação da referida importância.",
    y + 14
  );

  // Local e data
  const cityDate = `Cabreúva, ${new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}.`;
  doc.text(cityDate, 60, y + 40);

  // Assinatura
  const signY = y + 140;
  doc.setDrawColor(...INK);
  doc.line((W / 2) - 140, signY, (W / 2) + 140, signY);
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  doc.text("Assinatura do recebedor", W / 2, signY + 16, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(`${FISCAL.razaoSocial} — CNPJ ${FISCAL.cnpj}`, W / 2, signY + 32, { align: "center" });
  doc.text("Rede Hospeda — Gestão de Pousadas", W / 2, signY + 46, { align: "center" });

  // Rodapé
  doc.setDrawColor(...DIVIDER);
  doc.line(60, 780, W - 60, 780);
  doc.setTextColor(...MUTED);
  doc.setFontSize(8);
  doc.text(`Emitido em ${new Date().toLocaleString("pt-BR")}  ·  Reserva ${String(r.id).slice(0, 8).toUpperCase()}`, 60, 796);
  const footerRight = [r.pousada_telefone && `Tel: ${r.pousada_telefone}`, r.pousada_email]
    .filter(Boolean).join("   ·   ");
  if (footerRight) doc.text(footerRight, W - 60, 796, { align: "right" });

  const filename = `recibo-${(r.empresa_nome || r.hospede_nome || "reserva").replace(/\s+/g, "_")}-${r.data_entrada}.pdf`;
  doc.save(filename);
}

function _drawWordmarkFallback(doc) {
  doc.setTextColor(...PDF_BRAND.CREME);
  doc.setFont(PDF_BRAND.FONT_TITLE, "bold");
  doc.setFontSize(26);
  doc.text("REDE HOSPEDA", 50, 85);
}
